import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'primereact/card';
import { MarsinetContext } from '../../../context/marsinetProvider';
import { useToast } from '../../../context/ToastProvider';
import { analytics } from 'apps/marsinet/src/firebase';
import { logEvent } from 'firebase/analytics';
import VideosGrid from './components/videos-grid';
import { apiServices } from '../../../services/apiServices';
import { configureAxios } from '../../../utils/utils';
import VideoTagsManagementModal from './components/video-tags-management-modal';
import { Button } from 'primereact/button';
import { can, AvailableResourceActions } from '@marsinet/commons';

export interface VideoItem {
  id: string;
  filename: string;
  mimeType: string;
  fileSize: number;
  uploadedAt: Date;
  uploadedBy?: string;
  duration?: number;
  resolution?: string;
  tags?: string[];
  thumbnail?: string;
  title?: string;
  description?: string;
}

const VideosPage: React.FC = () => {
  const { t } = useTranslation();
  const { access_token, role, setError } = useContext(MarsinetContext);
  const { showToast: show } = useToast();
  const [loading, setLoading] = useState<boolean>(false);
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [showTagsManagementModal, setShowTagsManagementModal] = useState(false);
  const [totalVideos, setTotalVideos] = useState<number>(0);
  const [first, setFirst] = useState<number>(0);
  const [rows, setRows] = useState<number>(10);
  const [tagColors, setTagColors] = useState<Record<string, string>>({});

  const canManageTags =
    !!role && can(role, AvailableResourceActions.videos.manage_tags);

  useEffect(() => {
    if (!access_token) return;
    if (!role) return;
    configureAxios(access_token);
    fetchVideos();
  }, [access_token, role, first, rows]);



  const loadTags = async () => {
    try {
      const response =
        (await apiServices.videosApi.videosControllerGetAllTags()) as any;
      const tags = response.data?.tags || [];
      setTagColors(
        Object.fromEntries(
          tags.map((tag: any) => [tag.name, tag.color || '#3B82F6'])
        )
      );
    } catch (error) {
      console.error('Error loading tag colors:', error);
    }
  };

  useEffect(() => {
    if (!access_token || !canManageTags) return;
    configureAxios(access_token);

    loadTags();
  }, [access_token, canManageTags]);



  const fetchVideos = async () => {
    try {
      setLoading(true);

      const response = await apiServices.videosApi.videosControllerListVideos(
        rows.toString(),
        first.toString()
      );

      const data = response.data;

      const transformedVideos = data.videos.map((video: any) => ({
        id: video.id,
        filename: video.filename,
        mimeType: video.mimeType,
        fileSize: video.fileSize,
        uploadedAt: video.uploadedAt ? new Date(video.uploadedAt) : new Date(),
        uploadedBy: video.uploadedBy,
        duration: video.duration,
        resolution: video.resolution,
        tags: video.tags,
        thumbnail: video.thumbnail,
        title: video.title,
        description: video.description,
      })) as VideoItem[];
      setVideos(transformedVideos);
      setTotalVideos(data.total || 0);
    } catch (error) {
      console.error('[Videos] Error fetching videos:', error);
      setError(true);
      show(
        'error',
        t('common.error'),
        t('common.error_loading_videos'),
        3000
      );
      logEvent(analytics, 'error_marsinet', {
        error_message: error.message,
        error_page: window.location.pathname,
        timestamp: new Date().toISOString(),
        browser_info: navigator.userAgent,
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshTags = async () => {
    loadTags();
    fetchVideos();
  };


  return (
    <div className="p-6 w-full">
      <div className="col-12">
        <div className="surface-card p-3 shadow-3 border-round-xl flex justify-content-between align-items-center mb-5">
          <div>
            <div className="font-medium text-lg sm:text-xl md:text-2xl text-900">
              {t('common.videos')}
            </div>
            <p className="text-gray-500 mt-1 mb-0">
              {t('common.total_videos', { total: totalVideos })}
            </p>
          </div>
          {canManageTags && (
            <Button
              icon="pi pi-plus"
              label={t('common.add_tag_subscription')}
              onClick={() => setShowTagsManagementModal(true)}
              severity="secondary"
              aria-label={t('common.add_tag')}
              className="text-sm sm:text-base lg:text-lg px-3 py-2 lg:px-4 lg:py-2"
              tooltipOptions={{
                position: 'bottom',
                mouseTrack: true,
                mouseTrackTop: 20,
              }}
            />
          )}
        </div>
      </div>

      <Card>
        <VideosGrid
          videos={videos}
          loading={loading}
          canManageTags={canManageTags}
          tagColors={tagColors}
          totalRecords={totalVideos}
          rows={rows}
          first={first}
          onPageChange={(page) => setFirst(page * rows)}
          onRowsChange={(newRows) => {
            setRows(newRows);
            setFirst(0);
          }}
          onTagsUpdated={(updatedVideo) => {
            setVideos((prevVideos) =>
              prevVideos.map((v) =>
                v.id === updatedVideo.id ? updatedVideo : v
              )
            );
          }}
        />
      </Card>

      <VideoTagsManagementModal
        visible={showTagsManagementModal}
        onHide={() => {
          setShowTagsManagementModal(false);
        }}
        onRefreshVideos={refreshTags}
      />
    </div>
  );
};

export default VideosPage;